import { Controller } from "@hotwired/stimulus"

/**
 * JAP1 Progress Controller
 * Manages localStorage for tracking user progress across the JAP1 preparation mini-site
 */
export default class extends Controller {
  static targets = [
    "globalPercent",
    "globalBar",
    "questionsSeen",
    "questionsMastered",
    "questionsToReview",
    "chapitreBar",
    "chapitrePercent"
  ]

  static values = {
    storageKey: { type: String, default: "jap1_progress" }
  }

  connect() {
    this.loadProgress()
    this.updateUI()
  }

  // Load progress from localStorage
  loadProgress() {
    const stored = localStorage.getItem(this.storageKeyValue)
    if (stored) {
      try {
        this.progress = JSON.parse(stored)
      } catch (e) {
        console.error("Error parsing JAP1 progress:", e)
        this.progress = this.defaultProgress()
      }
    } else {
      this.progress = this.defaultProgress()
    }
  }

  // Default progress structure
  defaultProgress() {
    return {
      chapitres: {},
      stats: {
        questionsSeen: 0,
        questionsMastered: 0,
        questionsToReview: 0,
        studyTime: 0,
        lastVisit: null
      },
      examens: [],
      toReview: []
    }
  }

  // Save progress to localStorage
  saveProgress() {
    this.progress.stats.lastVisit = new Date().toISOString()
    localStorage.setItem(this.storageKeyValue, JSON.stringify(this.progress))
  }

  // Update all UI elements
  updateUI() {
    this.updateGlobalProgress()
    this.updateChapitreProgress()
  }

  // Update global progress indicators
  updateGlobalProgress() {
    const totalQuestions = 254
    const { questionsSeen, questionsMastered, questionsToReview } = this.calculateGlobalStats()

    const percent = Math.round((questionsMastered / totalQuestions) * 100)

    if (this.hasGlobalPercentTarget) {
      this.globalPercentTarget.textContent = `${percent}%`
    }

    if (this.hasGlobalBarTarget) {
      this.globalBarTarget.style.width = `${percent}%`
    }

    if (this.hasQuestionsSeenTarget) {
      this.questionsSeenTarget.textContent = questionsSeen
    }

    if (this.hasQuestionsMasteredTarget) {
      this.questionsMasteredTarget.textContent = questionsMastered
    }

    if (this.hasQuestionsToReviewTarget) {
      this.questionsToReviewTarget.textContent = questionsToReview
    }
  }

  // Calculate global stats from all chapitres
  calculateGlobalStats() {
    let questionsSeen = 0
    let questionsMastered = 0

    Object.values(this.progress.chapitres).forEach(chapitre => {
      if (chapitre.seen) questionsSeen += chapitre.seen.length
      if (chapitre.mastered) questionsMastered += chapitre.mastered.length
    })

    return {
      questionsSeen,
      questionsMastered,
      questionsToReview: this.progress.toReview?.length || 0
    }
  }

  // Update per-chapitre progress bars
  updateChapitreProgress() {
    // Nombre de questions par chapitre
    const questionsPerChapitre = {
      1: 12, 2: 8, 3: 17, 4: 11, 5: 9, 6: 8, 7: 5, 8: 25,
      9: 7, 10: 48, 11: 18, 12: 12, 13: 53, 14: 17, 15: 4
    }

    this.chapitreBarTargets.forEach(bar => {
      const chapitreId = bar.dataset.chapitreId
      if (chapitreId) {
        const chapitre = this.progress.chapitres[chapitreId] || { mastered: [] }
        const total = questionsPerChapitre[chapitreId] || 10
        const percent = Math.round((chapitre.mastered?.length || 0) / total * 100)
        bar.style.width = `${percent}%`
      }
    })

    this.chapitrePercentTargets.forEach(span => {
      const chapitreId = span.dataset.chapitreId
      if (chapitreId) {
        const chapitre = this.progress.chapitres[chapitreId] || { mastered: [] }
        const total = questionsPerChapitre[chapitreId] || 10
        const percent = Math.round((chapitre.mastered?.length || 0) / total * 100)
        span.textContent = `${percent}%`
      }
    })
  }

  // Mark a question as seen
  markAsSeen(chapitreId, questionIndex) {
    this.ensureChapitre(chapitreId)
    if (!this.progress.chapitres[chapitreId].seen.includes(questionIndex)) {
      this.progress.chapitres[chapitreId].seen.push(questionIndex)
      this.saveProgress()
      this.updateUI()
    }
  }

  // Mark a question as mastered
  markAsMastered(chapitreId, questionIndex) {
    this.ensureChapitre(chapitreId)
    const chapitre = this.progress.chapitres[chapitreId]

    // Remove from toReview if present
    this.progress.toReview = this.progress.toReview.filter(
      q => !(q.chapitreId === chapitreId && q.questionIndex === questionIndex)
    )

    // Add to mastered if not already
    if (!chapitre.mastered.includes(questionIndex)) {
      chapitre.mastered.push(questionIndex)
    }

    // Also mark as seen
    if (!chapitre.seen.includes(questionIndex)) {
      chapitre.seen.push(questionIndex)
    }

    this.saveProgress()
    this.updateUI()
  }

  // Mark a question for review
  markForReview(chapitreId, questionIndex) {
    this.ensureChapitre(chapitreId)

    // Remove from mastered
    const chapitre = this.progress.chapitres[chapitreId]
    chapitre.mastered = chapitre.mastered.filter(i => i !== questionIndex)

    // Add to toReview if not already
    const exists = this.progress.toReview.some(
      q => q.chapitreId === chapitreId && q.questionIndex === questionIndex
    )
    if (!exists) {
      this.progress.toReview.push({ chapitreId, questionIndex })
    }

    // Mark as seen
    if (!chapitre.seen.includes(questionIndex)) {
      chapitre.seen.push(questionIndex)
    }

    this.saveProgress()
    this.updateUI()
  }

  // Ensure chapitre structure exists
  ensureChapitre(chapitreId) {
    if (!this.progress.chapitres[chapitreId]) {
      this.progress.chapitres[chapitreId] = {
        seen: [],
        mastered: [],
        medium: []
      }
    }
  }

  // Get progress for a specific chapitre
  getChapitreProgress(chapitreId) {
    return this.progress.chapitres[chapitreId] || { seen: [], mastered: [], medium: [] }
  }

  // Add exam result
  addExamResult(type, score, duration) {
    this.progress.examens.push({
      date: new Date().toISOString(),
      type,
      score,
      duration
    })
    this.saveProgress()
  }

  // Export progress as JSON
  exportProgress() {
    const data = {
      version: "1.0",
      exportDate: new Date().toISOString(),
      progress: this.progress
    }
    return JSON.stringify(data, null, 2)
  }

  // Import progress from JSON
  importProgress(jsonString) {
    try {
      const data = JSON.parse(jsonString)
      if (data.progress) {
        this.progress = data.progress
        this.saveProgress()
        this.updateUI()
        return true
      }
    } catch (e) {
      console.error("Error importing progress:", e)
    }
    return false
  }

  // Reset all progress
  resetProgress() {
    this.progress = this.defaultProgress()
    this.saveProgress()
    this.updateUI()
  }
}
